// 변경공사 값 타입·행 — 목록은 `0026_change_order.sql` 의 check 와 한 글자도 다르면 안 된다(migrations 테스트가 맞춰 본다).

import type { OrgScopedRow } from "../common/types";
import type { VatMode } from "../money/vat";

export const CHANGE_ORDER_STATUSES = ["DRAFT", "SENT", "ACCEPTED", "REJECTED", "VOID"] as const;
export type ChangeOrderStatus = (typeof CHANGE_ORDER_STATUSES)[number];

export const CHANGE_ORDER_STATUS_LABEL: Record<ChangeOrderStatus, string> = {
  DRAFT: "작성 중",
  SENT: "승인 대기",
  ACCEPTED: "승인",
  REJECTED: "거절",
  VOID: "파기",
};

export const CHANGE_ORDER_DIRECTIONS = ["ADD", "DEDUCT"] as const;
export type ChangeOrderDirection = (typeof CHANGE_ORDER_DIRECTIONS)[number];

export const CHANGE_ORDER_DIRECTION_LABEL: Record<ChangeOrderDirection, string> = {
  ADD: "추가",
  DEDUCT: "감액",
};

export const CHANGE_ORDER_DECIDERS = ["CUSTOMER", "STAFF"] as const;
export type ChangeOrderDecider = (typeof CHANGE_ORDER_DECIDERS)[number];

export interface ChangeOrder extends OrgScopedRow {
  contract_id: string;
  project_id: string;
  seq: number;
  rev: number;
  status: ChangeOrderStatus;
  title: string;
  reason: string;
  source_log_id: string | null;
  extend_days: number | null;
  // 부가세 포함 증감액 — 감액이면 셋 다 음수.
  delta_total: number;
  delta_supply: number;
  delta_vat: number;
  base_total: number | null;
  after_total: number | null;
  snapshot_data: ChangeOrderSnapshotData | null;
  canonical: string | null;
  doc_hash: string | null;
  sent_at: string | null;
  decided_at: string | null;
  decided_by: ChangeOrderDecider | null;
  decided_name: string | null;
  decided_ip: string | null;
  decided_user_agent: string | null;
  decided_note: string | null;
  reject_reason: string | null;
  voided_at: string | null;
  void_reason: string | null;
  created_by: string | null;
  // 고객 링크 승인 손서명 파일 키(`0130`) — 서명 없이 승인했거나 옛 행이면 없다.
  accept_signature_key?: string | null;
}

export interface ChangeOrderItem extends OrgScopedRow {
  change_order_id: string;
  seq: number;
  direction: ChangeOrderDirection;
  item: string;
  unit: string;
  qty: number;
  material_unit_price: number;
  labor_unit_price: number;
  material_amount: number;
  labor_amount: number;
  amount: number;
  note: string;
  // 원 계약 견적 줄 연결(`0072`) — 옛 행·「추가 항목」 은 비어 있다.
  source_quote_item_id?: string | null;
  quote_trade_id?: string | null;
  space_code?: string | null;
  space_label?: string | null;
  trade_name?: string | null;
  source_label?: string | null;
}

export interface ChangeOrderDetail {
  change_order: ChangeOrder;
  items: ChangeOrderItem[];
}

// 문서 속 회차 한 줄 — 승인 RPC 가 seq 로 지금 행과 대조한다(★4).
export interface ChangeOrderSnapshotPayment {
  seq: number;
  label: string;
  amount: number;
  paid: boolean;
}

export interface ChangeOrderSnapshotLine {
  seq: number;
  direction: ChangeOrderDirection;
  item: string;
  unit: string;
  qty: number;
  material_unit_price: number;
  labor_unit_price: number;
  material_amount: number;
  labor_amount: number;
  amount: number;
  note: string;
  // 원 견적 줄 연결 — 값이 있을 때만 싣는다(옛 문서 해시가 그대로 남도록).
  source_quote_item_id?: string;
  trade_name?: string;
  space_label?: string;
  source_label?: string;
}

// 고객이 승인하는 문서 — 이 canonical 의 sha256 이 doc_hash 다.
export interface ChangeOrderSnapshotData {
  schema: 1;
  contract_no: string;
  contract_title: string;
  contract_doc_hash: string | null;
  client_name: string;
  contractor_name: string;
  seq: number;
  title: string;
  reason: string;
  extend_days: number | null;
  lines: ChangeOrderSnapshotLine[];
  delta_total: number;
  delta_supply: number;
  delta_vat: number;
  base_total: number;
  after_total: number;
  payments_before: ChangeOrderSnapshotPayment[];
  payments_after: ChangeOrderSnapshotPayment[];
  // 줄 단가의 부가세 기준 — 0098 전 문서엔 없다(changeOrderSnapshotVatModeOf 가 숫자로 추정).
  vat_mode?: VatMode;
}
